import type { CookieConsentApi } from '../common/cookie-consent'
import * as CookieConsent from 'vanilla-cookieconsent'
import { initCookieConsent } from '../common/cookie-consent'
import StorageKeys from '../common/storage-keys'
import 'vanilla-cookieconsent/dist/cookieconsent.css'

/** Server-rendered config, set inline in common/cookie-policy-script.blade.php. */
export interface DubbCookieConsentConfig {
  enableCookieFooter?: boolean
  message?: string
}

declare global {
  interface Window {
    dubbCookieConsentConfig?: DubbCookieConsentConfig
  }
}

const UTM_STORAGE_KEYS = Object.values(StorageKeys.UTM) as string[]

// The matching URL query parameters, e.g. `utm:source` -> `utm_source`.
const UTM_QUERY_PARAMS = UTM_STORAGE_KEYS.map(key => key.replace(':', '_'))

export function hasUtmData(): boolean {
  try {
    // Stored by configuration/utm-data.js on layouts that boot v4.js. On a first
    // landing (or layouts like `blank` that never run it) the keys are not stored
    // yet, so also check the current URL's query string directly.
    if (UTM_STORAGE_KEYS.some(key => window.localStorage.getItem(key) !== null)) {
      return true
    }

    const params = new URLSearchParams(window.location.search)

    return UTM_QUERY_PARAMS.some(param => params.has(param))
  }
  catch {
    return false
  }
}

/**
 * Whether tracking may be granted without a prompt. When the cookie footer is
 * disabled, GTM just loads (auto-accept). When it is enabled, the opt-in banner
 * is shown unless the visitor arrived with UTM data, in which case tracking is
 * granted automatically without a prompt.
 */
export function shouldAutoAccept(config: DubbCookieConsentConfig): boolean {
  return !config.enableCookieFooter || hasUtmData()
}

// Guarded so importing this module in tests has no side effects.
if (window.dubbCookieConsentConfig) {
  const config = window.dubbCookieConsentConfig

  initCookieConsent(CookieConsent as unknown as CookieConsentApi, {
    autoAccept: shouldAutoAccept(config),
    message: config.message,
  })
}
